<script setup lang="ts">
import {reactive, ref, useTemplateRef} from "vue";
import {useI18n} from "vue-i18n";
import {VueSpinner} from "vue3-spinners";
import {useHomepageStore} from "@/stores/homepageStore";
import * as DestinationAutocomplete from './composables/destinationsAutoComplete';
import DestinationPill from "@/components/Destination/DestinationPill.vue";
import {trackEvent} from "@/services/event.service";

const {t} = useI18n();
const props = defineProps({
  'tabindex':Number,
})

const homepageStore = useHomepageStore();
const useDestinationAutocomplete = reactive(DestinationAutocomplete.useDestinationAutocomplete());

const input = useTemplateRef('destinationInput');
const isInputFocused = ref(false);
const currentFocusIndex = ref(-1);


function focusRecommendation(index: number) {
  currentFocusIndex.value = index
  const recommendationsDivs = document.querySelectorAll('.result');
  if (recommendationsDivs[index]) {
    recommendationsDivs[index].focus();
    recommendationsDivs[index].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (!isInputFocused.value) return;
  const selectedRec = useDestinationAutocomplete.locations[currentFocusIndex.value];
  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      if (currentFocusIndex.value < useDestinationAutocomplete.locations.length-1 ) {
        focusRecommendation(currentFocusIndex.value + 1);
      } else {
        focusRecommendation(0);
      }
      break;
    case "ArrowUp":
      event.preventDefault();
      // Move up the list
      if (currentFocusIndex.value > 0) {
        focusRecommendation(currentFocusIndex.value - 1);
      } else {
        focusRecommendation(useDestinationAutocomplete.locations.length - 1);
      }
      break;
    case "Enter":
      event.preventDefault();
      onApply(selectedRec);
      input.value.blur();
      break;
    case "Escape":
      input.value.blur();
      homepageStore.current = 'none'
      break;
  }
}

function clickedParent() {
  homepageStore.current = 'destination';
  useDestinationAutocomplete.getPopularDestinations()
}


function onApply(location) {
  trackEvent('cosmosy.ga.mainSearch.popularDestination', {event_label: 'Popular Destination'});
  useDestinationAutocomplete.query = location.name;
  homepageStore.selections.location = location
  homepageStore.next()

}

function onDestinationClick() {
  trackEvent('cosmosy.ga.mainSearch.destinationField', {event_label: 'Destination Field'});
}

</script>

<template>
  <div class="relative w-1/2 h-full" :tabindex="props.tabindex" @keydown="handleKeydown">

    <div id="destination" @click="clickedParent"
         class="flex flex-col justify-between h-full py-4.5 pl-7.5 transition-all cursor-pointer">
      <div
          class="destination-container-content transition-all text-black/65 flex flex-row items-center gap-2  text-sm">
        <img src="@/assets/icons/svg/Location_16x16.svg" alt="Destination">
        <span>{{ t('search.destinationTitle') }}</span>
      </div>

      <div class="flex">
        <div class="flex-auto">
          <input ref="destinationInput"
                 @focusin="isInputFocused = true"
                 @focusout="isInputFocused = false"
                 v-model="useDestinationAutocomplete.query"
                 :placeholder="t('booking.selectDestination')"
                 class="destination-input text-black responsive-title-5 w-full outline-none placeholder-black focus:placeholder-bw-grey caret-current"
                 @change="onDestinationClick"
          />
        </div>
        <div class="w-14" v-show="useDestinationAutocomplete.loading">
          <VueSpinner size="30"
                      class="p-2"
                      color="#1B84FF"
          />

        </div>
      </div>
    </div>
    <transition name="slight-scroll">
      <div v-if="homepageStore.current == 'destination'"
           :class="`rounded-b-11xl absolute top-full w-full h-fit bg-white shadow-2xl z-10 border-t-2 border-primary-blue overflow-hidden`">
        <div class="overflow-y-scroll max-h-50">
          <div class="destination-picker-results" v-if="useDestinationAutocomplete.hasResults">
            <div v-for="(rec,index) in useDestinationAutocomplete.locations"
                 :key="rec.id"
                 :class="`result transition-all flex flex-row items-center justify-between pl-6 pr-3.5 py-2 cursor-pointer
                          ${currentFocusIndex === index ? 'selected-result' : ''}`"
                 @click="onApply(rec)">
              <div class="line-clamp-1 text-ellipsis">
                <span class="">{{ rec.name }}</span>
              </div>
              <DestinationPill :destination="rec"/>
            </div>
          </div>
          <div  v-if="!useDestinationAutocomplete.hasResults">
            <div class="result transition-all flex flex-row items-center justify-between pl-6 pr-3.5 py-2">
              <div>
                <span class="">{{ t('search.startTyping') }}</span>
              </div>
            </div>
          </div>
        </div>
        <div  v-if="useDestinationAutocomplete.hasPopular">
          <hr/>
          <div class="result transition-all flex flex-row items-center justify-between pl-6 pr-3.5 py-2">
            <div>
              <!-- TODO Translate -->
              <span class="font-bold">Popular Destinations</span>
            </div>
          </div>
        </div>
        <div class="overflow-y-scroll max-h-50">
          <div class="destination-picker-results" v-if="useDestinationAutocomplete.hasPopular">
            <div v-for="(rec,index) in useDestinationAutocomplete.popular"
                 :key="rec.id"
                 :class="`result transition-all flex flex-row items-center justify-between pl-6 pr-3.5 py-2 cursor-pointer
                          ${currentFocusIndex === index ? 'selected-result' : ''}`"
                 @click="onApply(rec)">
              <div class="line-clamp-1 text-ellipsis">
                <span class="">{{ rec.name }}</span>
              </div>
              <DestinationPill :destination="rec"/>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.result {
  margin-top: .5rem;
}

.destination-picker-results .result:hover {
  background-color: #ebf5ff;
}

.selected-result {
  background-color: #ebf5ff;
}

.elevated .destination-container-content {
  font-size: .75rem;
}

.destination-picker-results:has(.result):last-child {
  margin-bottom: 16px;
}

.border-black__hov:hover {
  border-color: #000 !important;
}

@media (min-width: 1536px) {
  .elevated .destination-container-content {
    font-size: 0.875rem;
  }
}
</style>
